import { FC, useEffect, useState } from 'react';
import Text from '@components/common/text';
import Button from '@components/common/button';
import ResponsiveModal from '@components/_atoms/ResponsiveModal';
import { useApplicationCertification } from '@repository/match';
import { useBasicInfo } from '@repository/users/useUsers';
import ApplyList from './applyList';
import { FontStyle } from '@domain/constant/fontStyle';
import { applyStatusTextMap } from '@domain/constant/jobApplicationProcessType';
import DateFormat from '@utils/date/format';
import usePdfMake, { getBase64ImageFromUrl } from '@utils/hooks/usePdfMake';
import TextFormat from '@utils/text/format';
import DateUtil from '@utils/date/util';
import classnames from 'classnames/bind';
import styles from './index.module.scss';

const cx = classnames.bind(styles);

interface SelectModalProps {
  visible: boolean;
  onClose: () => void;
}

const SelectModal: FC<SelectModalProps> = ({ visible, onClose }) => {
  const { data: userInfo } = useBasicInfo();
  const { downloadPdf } = usePdfMake();
  const [selectSns, setSelectSns] = useState<number[]>([]);
  const { mutateAsync: fetchCertification } = useApplicationCertification();
  const [stampBase64, setStampBase64] = useState<string | null>(null);
  const [logoBase64, setLogoBase64] = useState<string | null>(null);
  const pxToPt = (px: number) => px * 0.75;

  useEffect(() => {
    const fetchImage = async () => {
      const base64 = await getBase64ImageFromUrl('/img/mypage/certificate.png');
      if (base64) {
        setStampBase64(base64);
      }
      const logo64 = await getBase64ImageFromUrl('/img/mypage/logo.png');
      if (logo64) {
        setLogoBase64(logo64);
      }
    };
    fetchImage();
  }, []);

  const handleGeneratePdf = async () => {
    const data = await fetchCertification(selectSns);
    if (!data || !userInfo) return;

    const dateList = data.jobApplicationCertificationDtos
      .map((item) => item.applicationCompleteTime)
      .filter((item): item is string => item !== null) // Type guard to filter out null values
      .sort();

    const dateText = `${DateFormat.format(new Date(dateList[0]), 'yyyy년 MM월 dd일부터')} ${DateFormat.format(
      new Date(dateList[dateList.length - 1]),
      'yyyy년 MM월 dd일까지',
    )}`;

    const docDefinition = {
      pageSize: 'A4', // A4 사이즈로 설정
      content: [
        {
          columns: [
            {
              image: logoBase64,
              width: pxToPt(72), // 96px -> 포인트 단위
              height: pxToPt(16), // 16px -> 포인트 단위
              alignment: 'left',
            },
            { text: `발급번호 : ${data.certificationNumber}`, alignment: 'right', style: 'headerSn' },
          ],
        },
        { text: '취업활동 증명서', style: 'title', margin: [0, pxToPt(20), 0, pxToPt(40)] }, // 20px -> 포인트 단위
        {
          style: 'userInfoArea',
          table: {
            widths: ['auto', 'auto', '*'], // 포인트 단위 또는 비율로 지정 가능
            body: [
              [
                { text: '성명', style: 'label' },
                {
                  canvas: [
                    {
                      type: 'line',
                      x1: 0,
                      y1: pxToPt(4),
                      x2: 0,
                      y2: pxToPt(12),
                      lineWidth: pxToPt(1),
                      color: '#E7E8EA',
                    },
                  ],
                },
                { text: `${userInfo.name}`, style: 'value' },
              ],
              [
                { text: '생년월일', style: 'label' },
                {
                  canvas: [
                    {
                      type: 'line',
                      x1: 0,
                      y1: pxToPt(4),
                      x2: 0,
                      y2: pxToPt(12),
                      lineWidth: pxToPt(1),
                      color: '#E7E8EA',
                    },
                  ],
                },
                { text: `${userInfo.birthDate.replaceAll('-', '.')}`, style: 'value' },
              ],
              [
                { text: '연락처', style: 'label' },
                {
                  canvas: [
                    {
                      type: 'line',
                      x1: 0,
                      y1: pxToPt(4),
                      x2: 0,
                      y2: pxToPt(12),
                      lineWidth: pxToPt(1),
                      color: '#E7E8EA',
                    },
                  ],
                },
                { text: `${TextFormat.hypenMobile(userInfo?.mobile ?? '')}`, style: 'value' },
              ],
            ],
          },
          layout: 'noBorders',
          margin: [0, pxToPt(10), 0, pxToPt(30)], // 10px, 30px -> 포인트 단위
        },
        {
          style: 'applyInfoArea',
          table: {
            headerRows: 1,
            widths: ['14.49%', '23.19%', '47.83%', '14.49%'], // 비율로 지정
            dontBreakRows: true,
            keepWithHeaderRows: 1,
            body: [
              [
                { text: '지원일', style: 'tableHeader' },
                { text: '기업명', style: 'tableHeader' },
                { text: '주소', style: 'tableHeader' },
                { text: '활동결과', style: 'tableHeader' },
              ],
              ...data.jobApplicationCertificationDtos.map((item) => [
                {
                  text: item.applicationCompleteTime
                    ? DateFormat.format(new Date(item.applicationCompleteTime), 'yyyy.MM.dd')
                    : '-',
                  style: 'tableContent',
                },
                { text: item.companyName, style: 'tableContent' },
                { text: item.positionLocation ? item.positionLocation : '-', style: 'tableContent' },
                { text: applyStatusTextMap[item.jobApplicationProcessType], style: 'tableContent' },
              ]),
            ],
          },
          layout: {
            fillColor(rowIndex: any, node: any, columnIndex: any) {
              return rowIndex === 0 ? '#eeeeee' : null;
            },
            vLineWidth(i: any) {
              return 0;
            },
            hLineColor(i: any, node: any) {
              if (i === 0 || i === node.table.body.length) {
                return '#121619'; // 상단과 하단 보더 색
              }
              return '#e7e8ea'; // 나머지 보더 색
            },
            hLineWidth(i: any, node: any) {
              return pxToPt(1);
            },
            paddingLeft(i: any) {
              return i === 0 ? 0 : pxToPt(8); // 8px -> 포인트 단위
            },
            paddingRight(i: any, node: any) {
              return i === node.table.widths.length - 1 ? 0 : pxToPt(8); // 8px -> 포인트 단위
            },
            paddingTop(i: any) {
              return pxToPt(4); // 4px -> 포인트 단위
            },
            paddingBottom(i: any) {
              return pxToPt(4); // 4px -> 포인트 단위
            },
          },
          margin: [0, 0, 0, pxToPt(30)], // 30px -> 포인트 단위
        },
      ],
      styles: {
        header: {
          fontSize: pxToPt(24), // 24px -> 포인트 단위
          bold: true,
        },
        title: {
          fontSize: pxToPt(32), // 32px -> 포인트 단위
          bold: true,
          margin: [0, pxToPt(20), 0, pxToPt(20)], // 20px -> 포인트 단위
        },
        headerSn: {
          fontSize: pxToPt(10), // 10px -> 포인트 단위
          bold: false,
          color: '#9fa4ab',
        },
        userInfoArea: { margin: [0, pxToPt(16), 0, pxToPt(48)] }, // 16px, 48px -> 포인트 단위
        label: {
          width: pxToPt(50), // 50px -> 포인트 단위
          fontSize: pxToPt(12), // 12px -> 포인트 단위
          color: '#44474b',
        },
        divider: {
          width: pxToPt(1), // 1px -> 포인트 단위
          height: pxToPt(8), // 8px -> 포인트 단위
          backgroundColor: '#E7E8EA',
        },
        value: { color: '#44474b', fontSize: pxToPt(12) }, // 12px -> 포인트 단위
        applyInfoArea: { margin: [0, pxToPt(25), 0, pxToPt(25)] }, // 25px -> 포인트 단위
        tableHeader: {
          fontSize: pxToPt(9), // 12px -> 포인트 단위
          color: '#656a71',
          alignment: 'left',
          fillColor: '#F4F4F5',
          margin: [pxToPt(8), pxToPt(10), 0, pxToPt(10)], // 10px -> 포인트 단위
        },
        tableContent: {
          margin: [pxToPt(8), pxToPt(18), 0, pxToPt(18)],
          alignment: 'left',
          fontSize: pxToPt(10),
          color: '#121619',
        }, // 18px, 10px -> 포인트 단위
        footer: {
          fontSize: pxToPt(9), // 9px -> 포인트 단위
          bold: false,
          color: '#656A71',
          alignment: 'left',
        },
        footerRightTop: {
          fontSize: pxToPt(12), // 12px -> 포인트 단위
          bold: true,
          color: '#121619',
          alignment: 'right',
        },
        footerRight: {
          fontSize: pxToPt(9), // 9px -> 포인트 단위
          color: '#656A71',
          alignment: 'right',
        },
      },
      defaultStyle: { font: 'Pretendard' },
      footer: () => {
        return {
          columns: [
            {
              stack: [
                { text: dateText, style: 'footer', margin: [0, pxToPt(25), 0, 0] },
                { text: '잡다(https://www.jobda.im/)를 통해 위와 같이 취업 활동했음을 증명합니다.', style: 'footer' },
              ],
              alignment: 'right',
            },
            {
              stack: [
                { text: '자인원', style: 'footerRightTop', margin: [0, pxToPt(20), 0, 0] }, // 도장 이미지의 하단에 맞추기 위해 margin 조정
                {
                  text: DateFormat.format(DateUtil.getNow(), 'yyyy.MM.dd'),
                  style: 'footerRight',
                  margin: [0, 0, 0, 0],
                }, // 도장 이미지의 하단에 맞추기 위해 margin 조정
              ],
              alignment: 'right',
            },
            {
              image: stampBase64,
              width: pxToPt(50), // 50px -> 포인트 단위
              height: pxToPt(50), // 50px -> 포인트 단위
              alignment: 'right',
            },
          ],
          margin: [pxToPt(40), 0], // 40px -> 포인트 단위
        };
      },
      pageMargins: [pxToPt(40), pxToPt(50), pxToPt(40), pxToPt(100)],
    };
    const fileName = `취업증명서_${userInfo?.name}.pdf`;
    await downloadPdf(docDefinition, fileName);
  };

  return (
    <>
      <ResponsiveModal visible={visible} className={cx('layer')} onClickBg={onClose} escape={onClose}>
        <div className={cx('column')}>
          <Text fontStyle={FontStyle.H4_B}>취업활동 증명서 다운로드</Text>

          <ApplyList selectSns={selectSns} setSelectSns={setSelectSns} />
        </div>

        <div className={cx('btnArea')}>
          <Button
            className={cx('btn')}
            theme="secondary"
            onClick={() => {
              setSelectSns([]);
              onClose();
            }}
          >
            닫기
          </Button>
          <Button className={cx('btn')} onClick={handleGeneratePdf} disabled={!selectSns.length}>
            다운로드
          </Button>
        </div>
      </ResponsiveModal>
    </>
  );
};

export default SelectModal;
